import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Marche } from '@app/core/models/datawell/marche.model';
import { InvoiceAmountCalc } from '@app/core/models/invoices/invoice-amount-calc.model';
import { InvoiceDeductionDetail } from '@app/core/models/invoices/invoice-deduction.model';
import { InvoiceDetailModel } from '@app/core/models/invoices/invoice-detail.model';
import { InvoiceParameters } from '@app/core/models/invoices/invoice-parameters.model';
import { InvoiceViewType } from '@app/core/models/invoices/invoice-view-type.model';
import { DeductionTypeRef } from '@app/core/models/referentials/deduction-type.model';
import { BuildFormGroupsService } from '@app/core/services/manageInvoice/build-form-groups.service';
import { CommonLibraryService } from '@app/core/services/manageInvoice/common-library.service';
import { DeductionLibraryService } from '@app/core/services/manageInvoice/deduction-library.service';
import * as _ from 'lodash';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-invoice-deduction',
  templateUrl: './invoice-deduction.component.html',
  styleUrls: ['./invoice-deduction.component.scss'],
})
export class InvoiceDeductionComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  unsubscribe$: Subject<boolean> = new Subject<boolean>();

  @Input() invoiceDetailModel: InvoiceDetailModel;
  @Input() invoiceDetailAmountCalc: InvoiceAmountCalc;
  @Input() invoiceDetailForm: UntypedFormGroup;

  @Input() dropDownSettingsDeductionType: any;
  @Input() dropDownDataDeductionType: DeductionTypeRef[];
  @Input() dropDownDataDeductionTypeAll: DeductionTypeRef[];
  @Input() selectedMarche: Marche;

  @Input() invoiceParams: InvoiceParameters;
  @Input() isReadOnlyForm: boolean;
  @Input() isReadOnlyFormOrWaiting: boolean;
  @Input() submitted: boolean;

  @Input() editedDropdowns: string[];

  @Input() invoiceViewType: string;

  @Output() onSetSumInvoiceTaxIncluded: EventEmitter<void> =
    new EventEmitter<void>();
  @Output() onDeductionsChange: EventEmitter<UntypedFormArray> =
    new EventEmitter<UntypedFormArray>();
  @Output() onDeductionsPatched: EventEmitter<UntypedFormArray> =
    new EventEmitter<UntypedFormArray>();

  subcriptValueChanges: boolean = false;

  constructor(
    public commonLibrary: CommonLibraryService,
    public formInvoiceBuilder: BuildFormGroupsService,
    public deductionLibrary: DeductionLibraryService,
    private formBuilder: UntypedFormBuilder
  ) {}

  ngOnInit(): void {
    this.setValueChangeSubscription();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.patchDeductions();
    });
  }

  setValueChangeSubscription() {
    this.subcriptValueChanges = this.InvoiceDeductions != undefined && this.InvoiceDeductions.length > 0;

    if (this.subcriptValueChanges){
      this.InvoiceDeductions.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.onDeductionsChange.emit(this.InvoiceDeductions);
      });
    }
  }

  get InvoiceDeductions() {
    return this.invoiceDetailForm.controls['InvoiceDeductions'] as UntypedFormArray;
  }

  patchDeductions() {
    if (
      this.invoiceDetailModel.Deductions &&
      this.invoiceDetailModel.Deductions?.length > 0
    ) {
      this.invoiceDetailModel.Deductions.forEach((x) => {
        this.InvoiceDeductions.push(this.addDeductionFormGroup(x));
      });
      this.setSumDeduction();
    } else {
      this.addTemplateDeductions();
      this.setPercentage(this.selectedMarche, false);
    }
    this.formInvoiceBuilder.markFormGroupTouched(this.invoiceDetailForm);
    this.onDeductionsPatched.emit(this.InvoiceDeductions);
  }

  addDeduction() {
    const Deduction: InvoiceDeductionDetail = new InvoiceDeductionDetail();
    Deduction.CreatedAt = Date.now();
    this.invoiceDetailModel.Deductions.push(Deduction);

    this.InvoiceDeductions.push(this.addDeductionFormGroup(Deduction));

    this.formInvoiceBuilder.markFormGroupTouched(this.invoiceDetailForm);
    this.setSumDeduction();
    this.setValueChangeSubscription();
  }

  addTemplateDeductions() {
    if (
      (this.invoiceViewType == InvoiceViewType.DISPATCHED || this.invoiceViewType == InvoiceViewType.MANUAL) &&
      this.invoiceDetailModel.Deductions &&
      this.invoiceDetailModel.Deductions.length === 0
    ) {
      this.dropDownDataDeductionTypeAll
        .filter((e) => e.IsDefault)
        .forEach((element) => {
          const Deduction: InvoiceDeductionDetail =
            new InvoiceDeductionDetail();
          Deduction.CreatedAt =
            Date.now() + this.invoiceDetailModel.Deductions.length;
          Deduction.Code = element.Code;

          this.invoiceDetailModel.Deductions.push(Deduction);
          this.InvoiceDeductions.push(this.addDeductionFormGroup(Deduction));
        });
    }
  }

  addDeductionFormGroup(Deduction: InvoiceDeductionDetail): UntypedFormGroup {
    const selectedDeductionType = this.dropDownDataDeductionTypeAll.find(
      (x) => x.Code == Deduction.Code
    );
    return this.formInvoiceBuilder.buildDeductionFormGroup(
      Deduction,
      this.invoiceParams,
      this.isReadOnlyFormOrWaiting,
      this.isReadOnlyForm,
      selectedDeductionType
    );
  }

  setPercentage(
    currentdMarche: Marche,
    isLoadingExistingDeduction: boolean,
    invoiceAmoutIncluded?: string
  ) {
    this.selectedMarche = currentdMarche;
    if (this.selectedMarche) {
      this.InvoiceDeductions.controls.forEach((element, index) => {
        let currentForm = element as UntypedFormGroup;

        const createdAt = currentForm.controls.CreatedAt.value;
        const deductionCode = currentForm.controls.DeductionTypeCode.value;

        const jsonTag = _.find(
          this.dropDownDataDeductionTypeAll,
          (x) => x?.Code == deductionCode
        ).JsonTag;
        const percentage = this.selectedMarche[jsonTag];
        currentForm.controls.DeductionPercentage.setValue(percentage);

        if (!isLoadingExistingDeduction && percentage && percentage != 0) {
          currentForm.controls.AmountProvisionedTaxExcluded.setValue(
            this.deductionLibrary.getAmountByPercentage(
              percentage,
              invoiceAmoutIncluded
            )
          );
          this.setAmountOnDeduction(
            index,
            createdAt,
            'AmountProvisionedTaxExcluded'
          );
        }
      });
    }
  }

  deleteDeduction(index: number, CreatedAt: number) {
    const searchIndex: number = this.invoiceDetailModel.Deductions.findIndex(
      (x) => x.CreatedAt == CreatedAt
    );
    this.invoiceDetailModel.Deductions.splice(searchIndex, 1);
    this.InvoiceDeductions.removeAt(index);

    this.setSumDeduction();
    this.setValueChangeSubscription();
  }

  deleteAllDeduction() {
    this.invoiceDetailModel.Deductions = [];
    this.invoiceDetailForm.controls['InvoiceDeductions'] =
      this.formBuilder.array([]);

    this.setSumDeduction();
    this.setValueChangeSubscription();
  }

  onChangeDeductionType(
    index: number,
    createdAt: number,
    selectedDeductionType?: { Code: string } | null
  ): void {
    this.editedDropdowns.push('DeductionTypeCode');
    const _Datas = _.find(
      this.dropDownDataDeductionType,
      (x) => x?.Code === selectedDeductionType?.Code
    );

    var element = this.invoiceDetailModel.Deductions.find(
      (c) => c.CreatedAt == createdAt
    );
    element.Code = _Datas?.Code;
    element.Label = _Datas?.Label;
    element.AmountProvisionedTaxExcluded = null;
    element.AmountReleasedTaxExcluded = null;
    element.AmountRetainedTaxExcluded = null;

    var item = this.InvoiceDeductions.at(index) as UntypedFormGroup;
    item.controls.DeductionTypeCode.setValue(_Datas?.Code);
    item.controls.DeductionTypeLabel.setValue([_Datas]);
    item.controls.SelectedDeductionType.setValue([_Datas]);
    item.controls.AmountProvisionedTaxExcluded.setValue(null);
    item.controls.AmountReleasedTaxExcluded.setValue(null);
    item.controls.AmountRetainedTaxExcluded.setValue(null);

    !_Datas?.IsProvisionedActive ? item.controls.AmountProvisionedTaxExcluded.disable() : item.controls.AmountProvisionedTaxExcluded.enable();
    !_Datas?.IsReleasedActive ? item.controls.AmountReleasedTaxExcluded.disable() : item.controls.AmountReleasedTaxExcluded.enable();
    !_Datas?.IsRetainedActive ? item.controls.AmountRetainedTaxExcluded.disable() : item.controls.AmountRetainedTaxExcluded.enable();
  }

  setAmountOnDeduction(
    index: number,
    createdAt: number,
    formControlName: string
  ) {
    this.deductionLibrary.setAmountOnDeduction(
      index,
      createdAt,
      formControlName,
      this.InvoiceDeductions,
      this.invoiceDetailModel
    );
    this.setSumDeduction();
  }

  setSumDeduction() {
    this.deductionLibrary.setSumDeduction(
      this.invoiceDetailModel,
      this.invoiceDetailAmountCalc,
      this.dropDownDataDeductionTypeAll
    );
    this.onSetSumInvoiceTaxIncluded.emit();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(true);
  }
}
